/**
 * Theme generator — the heart of the white-label architecture.
 *
 * Takes a small set of BRAND INPUTS and derives the full semantic token set for
 * a given mode (light/dark), with every on-color computed to satisfy the WCAG
 * contrast ruleset (lib/contrast.ts). Components reference only semantic tokens,
 * so an org never edits 367 tokens or risks an unreadable theme — they edit ~10
 * inputs and this guarantees harmony + contrast.
 *
 * PURE + side-effect free. Applied at runtime by flatThemeToCssVars for
 * canonical and public event surfaces; the build gate measures this same module.
 */
import { generateBrandVars, deriveLinkColor, deriveFocusColor, deriveInkOnTint, isNativeBrandPalette } from './brand-colors'
import { bestTextOn, contrastRatio } from './contrast'
import { APP_THEME_VARS } from './design-tokens.generated'

export interface BrandInputs {
  /** Key brand color — drives primary actions, links, focus. */
  primary: string
  /** Accent for highlights/secondary emphasis (Nortura magenta). */
  accent?: string | null
  /** Page background base; light theme lightens it, dark theme darkens it. */
  surface?: string | null
  /** Font family name (resolved to a stack elsewhere). */
  font?: string | null
}

export type ThemeMode = 'light' | 'dark'

// Neutral ramps for surfaces/text/borders per mode. These are the system
// defaults; the surface input can shift the page background only.
// LIGHT mirrors the DS2 "Ink" ramp in index.css (:root) exactly — it had
// drifted to an old warm-gray ramp whose secondary text measured ~4.6:1 while
// the app's own floor is 7:1 (husregel 4), so a flat light theme without a
// custom surface read paler than every unbranded page.
const LIGHT = {
  page: '#F7F8FA', default: '#FFFFFF', muted: '#EEF1F5', elevated: '#FFFFFF',
  textPrimary: '#151A23', textSecondary: '#3A4354', textMuted: '#4C5567',
  borderDefault: '#E1E5EC', borderStrong: '#CDD3DD',
}
// DARK is DELIBERATELY warmer than the app's cool .dark ramp (#0E1116…): it is
// the white-label marble ramp — #0D0C0B is the tone of the Nortura dark-marble
// texture and the MARBLE_SCRIMS stops are mixed against it. Do not "align" it
// to index.css; a flat dark theme is a branded surface, not the app shell.
const DARK = {
  page: '#0D0C0B', default: '#1A1916', muted: '#26241F', elevated: '#1A1916',
  textPrimary: '#F5F5F5', textSecondary: '#A1A1AA', textMuted: '#71717A',
  borderDefault: '#2A2826', borderStrong: '#3A3733',
}

function isHex(v: string | null | undefined): v is string {
  return !!v && /^#[0-9a-fA-F]{6}$/.test(v)
}

// ── Small HSL helpers (local, so the surface input can drive the whole
// surface/text/border family rather than only the page background). ──
function toHsl(hex: string): [number, number, number] {
  const r = parseInt(hex.slice(1, 3), 16) / 255
  const g = parseInt(hex.slice(3, 5), 16) / 255
  const b = parseInt(hex.slice(5, 7), 16) / 255
  const max = Math.max(r, g, b), min = Math.min(r, g, b)
  let h = 0, s = 0
  const l = (max + min) / 2
  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break
      case g: h = ((b - r) / d + 2) / 6; break
      case b: h = ((r - g) / d + 4) / 6; break
    }
  }
  return [h * 360, s * 100, l * 100]
}
function fromHsl(h: number, s: number, l: number): string {
  h /= 360; s /= 100; l = Math.min(100, Math.max(0, l)) / 100
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1; if (t > 1) t -= 1
    if (t < 1 / 6) return p + (q - p) * 6 * t
    if (t < 1 / 2) return q
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
    return p
  }
  let r: number, g: number, b: number
  if (s === 0) { r = g = b = l } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s
    const p = 2 * l - q
    r = hue2rgb(p, q, h + 1 / 3); g = hue2rgb(p, q, h); b = hue2rgb(p, q, h - 1 / 3)
  }
  return '#' + [r, g, b].map((x) => Math.round(x * 255).toString(16).padStart(2, '0')).join('')
}
/** Shift a hex color's lightness by deltaL percentage points (clamped 0–100). */
function shiftL(hex: string, deltaL: number): string {
  const [h, s, l] = toHsl(hex)
  return fromHsl(h, s, l + deltaL)
}
/** Linear blend between two hex colors (t=0 → a, t=1 → b). */
function mix(a: string, b: string, t: number): string {
  const ah = a.replace('#', ''), bh = b.replace('#', '')
  const c = (i: number) => {
    const x = parseInt(ah.slice(i, i + 2), 16)
    const y = parseInt(bh.slice(i, i + 2), 16)
    return Math.round(x + (y - x) * t).toString(16).padStart(2, '0')
  }
  return `#${c(0)}${c(2)}${c(4)}`
}

/**
 * Derive a coherent surface + text + border family from a single page color,
 * with every text color forced WCAG-safe against the page (this is what makes a
 * custom navy/cream or any off-palette surface readable — no white-on-white).
 * `darkish` = page luminance is low, so cards lift; otherwise cards recede.
 */
function deriveSurfaceFamily(page: string) {
  const [, , l] = toHsl(page)
  const darkish = l < 50
  const on = bestTextOn(page).color // #FFFFFF or #111111 — max-contrast anchor
  const surfaceDefault = darkish ? shiftL(page, 6) : shiftL(page, 4)
  const surfaceMuted = darkish ? shiftL(page, 3) : shiftL(page, -3)
  const surfaceElevated = darkish ? shiftL(page, 9) : shiftL(page, 6)
  // Every surface text can land on, so text is derived against all of them
  // rather than against the page alone. See the note on `textMuted` below.
  const surfaces = [page, surfaceDefault, surfaceMuted, surfaceElevated]
  return {
    page,
    default: surfaceDefault,
    muted: surfaceMuted,
    elevated: surfaceElevated,
    textPrimary: ensureReadableOnAll(on, surfaces, 7),
    textSecondary: ensureReadableOnAll(mix(on, page, 0.32), surfaces, 4.5),
    // ── Fase 0 · the contract gap ACCESSIBILITY.md measured ──
    //
    // This was `ensureReadable(…, page, 3)`: a 3:1 floor, against the PAGE
    // only. Both halves were wrong, and the second half is the one that
    // actually shipped the bug.
    //
    // 3:1 is the floor for GRAPHICAL objects. `--text-muted` is used as text
    // 463 times in `src/` and as a graphical object twice (a loading dot and a
    // disabled toggle knob), so the token is small text and answers to 4.5:1.
    //
    // «Against the page only» is why axe found 4,4:1 on a surface the gate had
    // never looked at. Measured 16.08.2026 on the live «Nasjonalt salgsmøte»
    // theme: muted `#808387` scored 5,01 on surface-page — clear — and **4,40
    // on surface-default**, which is what a card paints. That is the exact pair
    // in ACCESSIBILITY.md («#808387 on #131e2e, 4,4:1»). The text never sat on
    // the surface the contract was measuring.
    //
    // Muted text lands on all four surfaces, so it is derived against all four.
    textMuted: ensureReadableOnAll(mix(on, page, 0.52), surfaces, 4.5),
    textInverted: darkish ? '#111827' : '#FFFFFF',
    borderDefault: mix(page, on, 0.14),
    borderStrong: mix(page, on, 0.26),
  }
}

/**
 * Adjust a foreground color so it meets a target contrast against bg, nudging
 * lightness toward white (on dark bg) or black (on light bg). Used for links,
 * which sit on a surface rather than a solid fill.
 */
function ensureReadable(fg: string, bg: string, target = 4.5): string {
  if (contrastRatio(fg, bg) >= target) return fg
  // Blend toward the readable on-color until target met (cheap, deterministic).
  const toward = bestTextOn(bg).color
  const fh = fg.replace('#', ''), th = toward.replace('#', '')
  for (let t = 0.15; t <= 1.0001; t += 0.15) {
    const mix = (i: number) => {
      const a = parseInt(fh.slice(i, i + 2), 16)
      const b = parseInt(th.slice(i, i + 2), 16)
      return Math.round(a + (b - a) * t).toString(16).padStart(2, '0')
    }
    const candidate = `#${mix(0)}${mix(2)}${mix(4)}`
    if (contrastRatio(candidate, bg) >= target) return candidate
  }
  return toward
}

/**
 * Hold a foreground above `target` against EVERY surface it can land on.
 *
 * `ensureReadable` answers one background. A text token answers four: the page,
 * the card, the muted fill and the elevated sheet. Deriving against the page
 * and shipping the result is how a token passes its gate at 5,01:1 and paints
 * 4,40:1 on the card the reader is actually looking at — see the note on
 * `textMuted` in `deriveSurfaceFamily`.
 *
 * The loop is bounded rather than recursive: each pass moves the colour further
 * toward the anchor of the surface it currently fails hardest against, and a
 * coherent family (all four within ±9 lightness of the page) converges in one
 * or two. The bound exists for the pathological case where the family straddles
 * the black/white anchor flip — there the last pass returns the most extreme
 * candidate it reached, and `contrastContract()` fails the build rather than
 * letting a silent near-miss through. A gate that catches it beats a loop that
 * hides it.
 */
function ensureReadableOnAll(fg: string, bgs: readonly string[], target: number): string {
  let out = fg
  for (let pass = 0; pass < 8; pass++) {
    let worstBg: string | null = null
    let worstRatio = Infinity
    for (const bg of bgs) {
      const r = contrastRatio(out, bg)
      if (r < worstRatio) { worstRatio = r; worstBg = bg }
    }
    if (worstBg === null || worstRatio >= target) return out
    const next = ensureReadable(out, worstBg, target)
    // No movement left — the anchor itself is as far as this colour goes.
    if (next === out) return out
    out = next
  }
  return out
}

/**
 * Derive the full semantic token map for one mode from the brand inputs.
 * Returns CSS-var name → hex. Every *-text / on-* value is contrast-correct.
 */
export function generateSemanticTokens(inputs: BrandInputs, mode: ThemeMode): Record<string, string> {
  // A native preset has no custom surface or heading face. Its canonical roles
  // come from DS2, so it cannot silently become an older generic neutral ramp.
  if (isNativeBrandPalette(inputs.primary, inputs.accent) && !inputs.surface &&
      (!inputs.font || inputs.font.trim().toLowerCase() === 'inter')) {
    return { ...APP_THEME_VARS[mode] }
  }
  const ramp = mode === 'dark' ? DARK : LIGHT
  // When the brand supplies a surface, derive the WHOLE surface/text/border
  // family from it (WCAG-safe) instead of only shifting the page background.
  // Otherwise fall back to the fixed system ramp for the mode.
  const fam = isHex(inputs.surface)
    ? deriveSurfaceFamily(inputs.surface)
    : (() => {
        // The FIXED ramps answer to the same law as the derived family. They
        // are hand-authored hexes, so «is this readable on the card?» was a
        // thing a person had to get right by eye, once, and then keep right —
        // and DARK's `#71717A` measured 3,21:1 on `--surface-muted`. Running
        // the ramp through the same guarantee makes it correct BY
        // CONSTRUCTION: edit the constant to any hex you like and the floor
        // still holds. A no-op for LIGHT, which already cleared every pair.
        const surfaces = [ramp.page, ramp.default, ramp.muted, ramp.elevated]
        return {
          page: ramp.page, default: ramp.default, muted: ramp.muted, elevated: ramp.elevated,
          textPrimary: ensureReadableOnAll(ramp.textPrimary, surfaces, 7),
          textSecondary: ensureReadableOnAll(ramp.textSecondary, surfaces, 4.5),
          textMuted: ensureReadableOnAll(ramp.textMuted, surfaces, 4.5),
          textInverted: mode === 'dark' ? '#111827' : '#FFFFFF',
          borderDefault: ramp.borderDefault, borderStrong: ramp.borderStrong,
        }
      })()

  // Links and focus land on cards and sheets as well as the page. Resolve
  // against the whole family, preserving the shared hue and drift contract.
  const surfaces = [fam.page, fam.default, fam.muted, fam.elevated]
  const link = deriveLinkColor(inputs.primary, surfaces, mode === 'dark' ? 'dark' : 'light')
  const focus = deriveFocusColor(inputs.primary, surfaces)

  const vars: Record<string, string> = {
    // Primary scale + action/button + their (contrast-correct) on-text.
    ...generateBrandVars(inputs.primary),

    // Surfaces.
    '--surface-page': fam.page,
    '--surface-default': fam.default,
    '--surface-muted': fam.muted,
    '--surface-elevated': fam.elevated,
    '--card-bg': fam.default,
    '--input-bg': fam.default,

    // Text — on-colors for the page surface.
    '--text-primary': fam.textPrimary,
    '--text-secondary': fam.textSecondary,
    '--text-muted': fam.textMuted,
    '--text-inverted': fam.textInverted,

    // Borders.
    '--border-default': fam.borderDefault,
    '--border-strong': fam.borderStrong,
    '--card-border': fam.borderDefault,
    '--input-border': fam.borderDefault,
    // 30b·2 — a focus indicator is graphical: 3:1 against the surface, and it
    // never keeps the raw brand hex if that hex is invisible on it.
    '--border-focus': focus,
    '--focus-ring-color': focus,

    // Links: 4.5:1 on every surface, hue and saturation kept, with 30b·4's
    // escape hatch when the compliant colour would no longer read as the brand.
    '--text-link': link.color,
    '--text-link-underline': link.underline,
    '--text-link-decoration': link.decoration,
    // Text- and icon-bearing brand roles take the same checked colour. Inline on <html> without a
    // [data-brand-scope], the `.dark` rule otherwise resolved them to the raw primary-500: the
    // active bottom-tab label read #b85151 on the PROFF theme in dark, 2.7:1 (30.10.2026).
    '--color-interactive-primary': link.color,
    '--icon-primary': link.color,

    // On-colors for fixed status fills.
    '--on-success': '#FFFFFF',
  }

  // ── The subtle brand callout: emit BOTH ends, or neither is guaranteed ──
  // The tint is mixed off the page so it stays subtle in either mode; the ink
  // is then derived against that exact tint. Emitting only the tint is what
  // broke: components paired it with `--color-primary-700`, which in a dark
  // theme is also dark, and the pair measured 1.71:1 on the live Nortura brand
  // while `auditGeneratedTheme` reported zero failures because no ramp-on-ramp
  // pair was in its six-pair list. Both ends now move together, always.
  const brandTint = mix(fam.page, inputs.primary, mode === 'dark' ? 0.18 : 0.9)
  vars['--surface-brand-subtle'] = brandTint
  vars['--text-on-brand-subtle'] = deriveInkOnTint(inputs.primary, brandTint)

  if (isHex(inputs.accent)) {
    // Drive the full accent scale off the input so accent-tinted surfaces stay
    // on-palette, and compute a contrast-correct on-accent text.
    const [ah, as] = toHsl(inputs.accent)
    vars['--color-accent-500'] = inputs.accent
    vars['--color-accent-400'] = fromHsl(ah, as, Math.min(toHsl(inputs.accent)[2] + 10, 92))
    vars['--on-accent'] = bestTextOn(inputs.accent).color
    vars['--color-accent-600'] = ensureReadable(fromHsl(ah, as, Math.max(toHsl(inputs.accent)[2] - 10, 14)), vars['--on-accent'], 4.5)
  }
  // No accent override means both fill and ink inherit from the enclosing
  // theme. Emitting white ink alone made the default teal fail at 2.49:1.

  // Dark mode: the light-tint shades (50/100/200) are used across the app as
  // SUBTLE selected / hover / active backgrounds (e.g. `bg-primary-50` +
  // `text-primary`). generateBrandVars makes them near-WHITE (fixed lightness),
  // which becomes white-on-white once text flips to light under a dark theme.
  // Remap them to dark, primary/accent-tinted surfaces so token-colored text
  // stays readable everywhere those shades are used — fixes the whole class.
  if (mode === 'dark') {
    vars['--color-primary-50'] = mix(fam.page, inputs.primary, 0.18)
    vars['--color-primary-100'] = mix(fam.page, inputs.primary, 0.28)
    vars['--color-primary-200'] = mix(fam.page, inputs.primary, 0.42)
    if (isHex(inputs.accent)) {
      vars['--color-accent-50'] = mix(fam.page, inputs.accent, 0.18)
      vars['--color-accent-100'] = mix(fam.page, inputs.accent, 0.28)
    }
  }

  return vars
}

/** Use the same complete role contract in diagnostics and the build gate. */
export function auditGeneratedTheme(inputs: BrandInputs, mode: ThemeMode): { pair: string; ratio: number }[] {
  return contrastContract(inputs, mode)
    .map(({ pair, fg, bg, floor }) => ({ pair, ratio: contrastRatio(fg, bg), floor }))
    .filter(({ ratio, floor }) => !(ratio >= floor))
    .map(({ pair, ratio }) => ({ pair, ratio }))
}

/**
 * Every foreground/background pair a generated theme must keep readable, with
 * the floor each one answers to. The gate (scripts/check-theme-contrast.mjs)
 * runs this over every live tenant brand plus hostile edge cases.
 *
 * This list is the contract. A pair that is not here is not guaranteed, which
 * is not a theoretical gap: the six-pair `auditGeneratedTheme` list passed the
 * Nortura theme while its own banner sat at 1.71:1. Adding a role to the
 * generator without adding it here recreates that hole exactly.
 *
 * Floors follow the house rules: 4.5 for body text, 3 for graphical objects
 * and large text, 7 where the app already holds itself to the higher bar.
 */
export function contrastContract(inputs: BrandInputs, mode: ThemeMode): { pair: string; fg: string; bg: string; floor: number }[] {
  const v = generateSemanticTokens(inputs, mode)
  const linkPaints = v['--text-link'] === 'var(--text-primary)' ? v['--text-primary'] : v['--text-link']
  return [
    // ── Text × surface, exhaustive ──
    //
    // Fase 0. This block used to be five hand-picked pairs, and the picking was
    // the hole: `text-muted` was measured on `surface-page` at floor 3, and on
    // nothing else. Measured 16.08.2026, live «Nasjonalt salgsmøte» theme —
    // muted scored 5,01 on the page and 4,40 on `surface-default`. The gate was
    // green; axe found seven contrast nodes on Svar and six on Deltakere.
    //
    // Three text roles × four surfaces is twelve pairs, so it is now written as
    // the product rather than as a selection. A surface added to the generator
    // and not added here recreates exactly the omission this replaces.
    ...(['--surface-page', '--surface-default', '--surface-muted', '--surface-elevated'] as const).flatMap(
      (surface) => [
        // 7:1 is the house floor for primary text (husregel 4), not the WCAG AA 4.5.
        { pair: `text-primary on ${surface.replace('--surface-', 'surface-')}`, fg: v['--text-primary'], bg: v[surface], floor: 7 },
        { pair: `text-secondary on ${surface.replace('--surface-', 'surface-')}`, fg: v['--text-secondary'], bg: v[surface], floor: 4.5 },
        // 4,5 — NOT 3. `--text-muted` is small text in 461 of its 463 uses; the
        // two graphical ones (a loading dot, a disabled toggle knob) are held to
        // a stricter floor than they need, which costs two pixels of tone and
        // saves a second token nobody would keep in sync.
        { pair: `text-muted on ${surface.replace('--surface-', 'surface-')}`, fg: v['--text-muted'], bg: v[surface], floor: 4.5 },
        { pair: `text-link on ${surface.slice(2)}`, fg: linkPaints, bg: v[surface], floor: 4.5 },
        { pair: `border-focus on ${surface.slice(2)}`, fg: v['--border-focus'], bg: v[surface], floor: 3 },
      ],
    ),
    { pair: 'action-primary-text on action-primary-bg', fg: v['--action-primary-text'], bg: v['--action-primary-bg'], floor: 4.5 },
    { pair: 'btn-primary-text on btn-primary-bg', fg: v['--btn-primary-text'], bg: v['--btn-primary-bg'], floor: 4.5 },
    ...['--btn-primary-bg-hover', '--btn-primary-bg-active'].map((bg) => ({
      pair: `btn-primary-text on ${bg.slice(2)}`, fg: v['--btn-primary-text'], bg: v[bg], floor: 4.5,
    })),
    { pair: 'action-primary-text on action-primary-bg-hover', fg: v['--action-primary-text'], bg: v['--action-primary-bg-hover'], floor: 4.5 },
    { pair: 'text-on-brand-subtle on surface-brand-subtle', fg: v['--text-on-brand-subtle'], bg: v['--surface-brand-subtle'], floor: 4.5 },
    ...(v['--color-accent-500']
      ? ['--color-accent-500', '--color-accent-600'].map(bg => ({ pair: `on-accent on ${bg.slice(2)}`, fg: v['--on-accent'], bg: v[bg], floor: 4.5 }))
      : []),
  ]
}
